import { FormContent, FormFields } from "@/components/page-canvas";
import { RecordLink, RecordTitle } from "@/components/record-link";
import { Form, Head, Link } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import SettingsLayout from "@/layouts/settings_layout";
import { locationSettingsLocationPath, userLocationsPath } from "@/routes";

type Location = {
  id: string;
  name: string;
  city?: string;
  country_code: string;
  currency: string;
  time_zone: string;
  deactivated_at: string | null;
};
type Country = {
  label: string;
  value: string;
  currency: string;
  time_zone: string;
};
type TimeZone = { label: string; value: string };

export default function Locations({
  locations,
  countries,
  time_zones: timeZones,
  can_create: canCreate,
}: {
  locations: Location[];
  countries: Country[];
  time_zones: TimeZone[];
  can_create: boolean;
}) {
  const { t } = useTranslation();
  return (
    <SettingsLayout>
      <Head title={t("common.locations")} />
      <div className="space-y-6">
        <header>
          <h1 className="text-xl font-semibold">{t("common.locations")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("locations_ui.description")}
          </p>
        </header>
        <div className="grid gap-4 @min-[720px]/settings-workspace:grid-cols-2">
          {locations.map((location) => (
            <Card key={location.id}>
              <CardHeader>
                <CardTitle>
                  <RecordLink
                    href={
                      location.deactivated_at
                        ? `/user/location_histories/${location.id}`
                        : locationSettingsLocationPath(location.id)
                    }
                  >
                    <RecordTitle>{location.name}</RecordTitle>
                  </RecordLink>
                </CardTitle>
                <CardDescription>
                  {[location.city, location.country_code]
                    .filter(Boolean)
                    .join(", ")}{" "}
                  · {location.currency} · {location.time_zone}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  nativeButton={false}
                  role="link"
                  render={
                    <Link
                      href={
                        location.deactivated_at
                          ? `/user/location_histories/${location.id}`
                          : locationSettingsLocationPath(location.id)
                      }
                    />
                  }
                >
                  {t(
                    location.deactivated_at
                      ? "resources.retained_history"
                      : "common.settings",
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card data-layout-frame="">
          <CardHeader>
            <CardTitle>{t("locations_ui.add")}</CardTitle>
            <CardDescription>
              {canCreate
                ? t("locations_ui.new_description")
                : t("locations_ui.limit")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form action={userLocationsPath()} method="post" resetOnSuccess>
              {({ errors, processing }) => (
                <FormContent>
                  <FieldGroup>
                    <Field>
                      <FieldLabel htmlFor="new_location_name">
                        {t("fields.name")}
                      </FieldLabel>
                      <Input
                        id="new_location_name"
                        name="location[name]"
                        required
                      />
                      <FieldError>{errors.name?.[0]}</FieldError>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="new_location_country">
                        {t("fields.country_code")}
                      </FieldLabel>
                      <NativeSelect
                        id="new_location_country"
                        name="location[country_code]"
                        defaultValue={countries[0]?.value}
                      >
                        {countries.map((country) => (
                          <NativeSelectOption
                            key={country.value}
                            value={country.value}
                          >
                            {t(`countries.${country.value}`, {
                              defaultValue: country.label,
                            })}
                          </NativeSelectOption>
                        ))}
                      </NativeSelect>
                    </Field>
                    <FormFields>
                      <Field>
                        <FieldLabel htmlFor="new_location_currency">
                          {t("fields.currency")}
                        </FieldLabel>
                        <Input
                          id="new_location_currency"
                          name="location[currency]"
                          defaultValue={countries[0]?.currency || "USD"}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="new_location_locale">
                          {t("common.language")}
                        </FieldLabel>
                        <NativeSelect
                          id="new_location_locale"
                          name="location[locale]"
                          defaultValue="en"
                        >
                          <NativeSelectOption value="en">
                            English
                          </NativeSelectOption>
                          <NativeSelectOption value="ro">
                            Română
                          </NativeSelectOption>
                        </NativeSelect>
                        <FieldError>{errors.locale?.[0]}</FieldError>
                      </Field>
                    </FormFields>
                    <Field>
                      <FieldLabel htmlFor="new_location_timezone">
                        {t("fields.time_zone")}
                      </FieldLabel>
                      <NativeSelect
                        id="new_location_timezone"
                        name="location[time_zone]"
                        defaultValue={countries[0]?.time_zone}
                      >
                        {timeZones.map((zone) => (
                          <NativeSelectOption
                            key={zone.value}
                            value={zone.value}
                          >
                            {zone.label}
                          </NativeSelectOption>
                        ))}
                      </NativeSelect>
                    </Field>
                    {Object.entries(errors)
                      .filter(([key]) => !["name", "locale"].includes(key))
                      .map(([key, messages]) => (
                        <FieldError key={key}>{messages}</FieldError>
                      ))}
                    <Button
                      type="submit"
                      className="w-fit"
                      disabled={!canCreate || processing}
                    >
                      {processing && <Spinner />}
                      {t("locations_ui.add")}
                    </Button>
                  </FieldGroup>
                </FormContent>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
